// # 教程管理
<template>
  <div class="tutorial-manage">
    <div class="header">
      <h2>教程管理</h2>
      <el-button type="primary" @click="showAddDialog">添加教程</el-button>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
        v-model="searchQuery"
        placeholder="搜索教程标题"
        class="search-input"
        clearable
        @clear="handleSearch"
        @input="handleSearch"
      />
      <el-button type="primary" @click="handleSearch">查询</el-button>
    </div>

    <!-- 教程列表 -->
    <el-table :data="tutorials" style="width: 100%" v-loading="loading">
      <el-table-column prop="title" label="标题" />
      <el-table-column label="封面图" width="120">
        <template #default="{ row }">
          <el-image 
            v-if="row.image_type === 'upload'"
            :src="`/api/uploads/tutorials/${row.image_path}`"
            :preview-src-list="[`/api/uploads/tutorials/${row.image_path}`]"
            fit="cover"
            class="preview-image"
          />
          <el-image 
            v-else-if="row.image_url"
            :src="row.image_url"
            :preview-src-list="[row.image_url]"
            fit="cover"
            class="preview-image"
          />
        </template>
      </el-table-column>
      <el-table-column prop="content" label="内容" show-overflow-tooltip />
      <el-table-column label="操作" width="200">
        <template #default="{ row }">
          <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
          <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 添加/编辑对话框 -->
    <el-dialog 
      :title="dialogType === 'add' ? '添加教程' : '编辑教程'" 
      v-model="dialogVisible"
      width="60%"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="内容" prop="content">
          <el-input type="textarea" v-model="form.content" rows="6" />
        </el-form-item>
        <el-form-item label="图片方式" prop="image_type">
          <el-radio-group v-model="form.image_type">
            <el-radio value="upload">文件上传</el-radio>
            <el-radio value="url">外部链接</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item 
          :label="form.image_type === 'upload' ? '上传图片' : '图片链接'" 
          :prop="form.image_type === 'upload' ? 'image' : 'image_url'"
        >
          <template v-if="form.image_type === 'upload'">
            <el-upload
              class="upload-demo"
              :action="`/api/admin/content/tutorials`"
              :headers="uploadHeaders"
              :on-success="handleUploadSuccess"
              :on-error="handleUploadError"
              :before-upload="beforeUpload"
              :limit="1"
              :file-list="fileList"
              :auto-upload="false"
              :on-change="handleFileChange"
              ref="uploadRef"
              name="image"
              accept="image/jpeg,image/png,image/webp"
            >
              <template #trigger>
                <el-button type="primary">选择图片</el-button>
              </template>
              <template #tip>
                <div class="el-upload__tip">
                  支持 jpg/png/webp 格式，大小不超过5MB
                </div>
              </template>
            </el-upload>
          </template>
          <template v-else>
            <el-input v-model="form.image_url" placeholder="输入图片URL" />
          </template>
        </el-form-item>
        <el-form-item label="视频方式" prop="video_type">
          <el-radio-group v-model="form.video_type">
            <el-radio value="upload">文件上传</el-radio>
            <el-radio value="url">外部链接</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item 
          :label="form.video_type === 'upload' ? '上传视频' : '视频链接'" 
          :prop="form.video_type === 'upload' ? 'video' : 'video_url'"
        >
          <template v-if="form.video_type === 'upload'">
            <el-upload
              class="upload-demo"
              :action="`/api/admin/content/tutorials`"
              :headers="uploadHeaders"
              :on-success="handleVideoUploadSuccess"
              :on-error="handleUploadError"
              :before-upload="beforeVideoUpload"
              :limit="1"
              :file-list="videoFileList"
              :auto-upload="false"
              :on-change="handleVideoChange"
              ref="videoUploadRef"
              name="video"
              accept="video/mp4,video/webm"
            >
              <template #trigger>
                <el-button type="primary">选择视频</el-button>
              </template>
              <template #tip>
                <div class="el-upload__tip">
                  支持 mp4/webm 格式，大小不超过50MB
                </div>
              </template>
            </el-upload>
          </template>
          <template v-else>
            <el-input v-model="form.video_url" placeholder="输入视频URL" />
          </template>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '@/utils/request'

export default {
  name: 'TutorialManage',
  setup() {
    const tutorials = ref([])
    const loading = ref(false)
    const dialogVisible = ref(false)
    const dialogType = ref('add')
    const submitting = ref(false)
    const formRef = ref(null)
    const currentId = ref(null)
    const fileList = ref([])
    const videoFileList = ref([])
    const uploadRef = ref(null)
    const videoUploadRef = ref(null)
    const currentFile = ref(null)
    const currentVideo = ref(null)

    const form = ref({
      title: '',
      content: '',
      image_type: 'upload',
      image_url: '',
      image: null,
      video_type: 'upload',
      video_url: '',
      video: null
    })

    const rules = {
      title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
      content: [{ required: true, message: '请输入内容', trigger: 'blur' }],
      image_type: [{ required: true, message: '请选择图片方式', trigger: 'change' }],
      image: [{ required: true, message: '请上传图片', trigger: 'change' }],
      image_url: [{ required: true, message: '请输入图片链接', trigger: 'blur' }],
      video_type: [{ required: true, message: '请选择视频方式', trigger: 'change' }],
      video: [{ required: true, message: '请上传视频', trigger: 'change' }],
      video_url: [{ required: true, message: '请输入视频链接', trigger: 'blur' }]
    }

    // 搜索和筛选
    const searchQuery = ref('')

    // 分页相关
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)

    // 获取教程列表
    const fetchTutorials = async () => {
      loading.value = true
      try {
        const res = await request.get('/admin/content/tutorials')
        tutorials.value = res
      } catch (error) {
        console.error('获取教程列表失败:', error)
        ElMessage.error('获取教程列表失败')
      } finally {
        loading.value = false
      }
    }

    // 显示添加对话框
    const showAddDialog = () => {
      dialogType.value = 'add'
      form.value = {
        title: '',
        content: '',
        image_type: 'upload',
        image_url: '',
        image: null,
        video_type: 'upload',
        video_url: '',
        video: null
      }
      fileList.value = []
      videoFileList.value = []
      currentFile.value = null
      currentVideo.value = null
      dialogVisible.value = true
    }

    // 处理编辑
    const handleEdit = (row) => {
      dialogType.value = 'edit'
      currentId.value = row.id
      form.value = {
        title: row.title || '',
        content: row.content || '',
        image_type: row.image_type || 'upload',
        image_url: row.image_url || '',
        image: null,
        video_type: row.video_type || 'upload',
        video_url: row.video_url || '',
        video: null
      }
      
      if (row.image_type === 'upload' && row.image_path) {
        fileList.value = [{
          name: row.image_path,
          url: `/api/uploads/tutorials/${row.image_path}`
        }]
      } else {
        fileList.value = []
      }

      if (row.video_type === 'upload' && row.video_path) {
        videoFileList.value = [{
          name: row.video_path,
          url: `/api/uploads/tutorials/${row.video_path}`
        }]
      } else {
        videoFileList.value = []
      }

      currentFile.value = null
      currentVideo.value = null
      dialogVisible.value = true
    }

    // 处理删除
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确定要删除这个教程吗？', '提示', {
          type: 'warning'
        })
        await request.delete(`/admin/content/tutorials/${row.id}`)
        ElMessage.success('删除成功')
        fetchTutorials()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }

    // 提交表单
    const handleSubmit = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        submitting.value = true
        
        const formData = new FormData()
        
        // 添加基本字段
        formData.append('title', form.value.title || '')
        formData.append('content', form.value.content || '')
        
        // 获取管理员信息
        const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
        if (!adminInfo || !adminInfo.id) {
          throw new Error('未找到管理员信息，请重新登录')
        }
        formData.append('admin_id', adminInfo.id)

        // 处理图片
        if (form.value.image_type === 'upload') {
          if (!currentFile.value) {
            throw new Error('请选择图片')
          }
          formData.append('image', currentFile.value)
        } else {
          if (!form.value.image_url) {
            throw new Error('请输入图片链接')
          }
          formData.append('image_url', form.value.image_url)
        }

        // 处理视频
        if (form.value.video_type === 'upload') {
          if (currentVideo.value) {
            formData.append('video', currentVideo.value)
          }
        } else {
          if (form.value.video_url) {
            formData.append('video_url', form.value.video_url)
          }
        }
        
        try {
          if (dialogType.value === 'add') {
            const response = await request.post('/admin/content/tutorials', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            })
            ElMessage.success('添加成功')
          } else {
            const response = await request.put(`/admin/content/tutorials/${currentId.value}`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            })
            ElMessage.success('更新成功')
          }
          
          dialogVisible.value = false
          fetchTutorials()
        } catch (submitError) {
          console.error('提交请求错误:', submitError)
          if (submitError.response) {
            throw new Error(submitError.response.data.message || submitError.response.data.error || '提交失败')
          } else if (submitError.request) {
            throw new Error('服务器无响应，请检查网络连接')
          } else {
            throw new Error(`提交失败: ${submitError.message || '未知错误'}`)
          }
        }
      } catch (error) {
        console.error('表单提交失败:', error)
        ElMessage.error(error.message || (dialogType.value === 'add' ? '添加失败' : '更新失败'))
      } finally {
        submitting.value = false
      }
    }

    // 上传相关
    const uploadHeaders = computed(() => ({
      Authorization: `Bearer ${localStorage.getItem('adminToken')}`
    }))

    const beforeUpload = (file) => {
      const isImage = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type)
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isImage) {
        ElMessage.error('只能上传 jpg/png/webp 格式的图片!')
        return false
      }
      if (!isLt5M) {
        ElMessage.error('图片大小不能超过 5MB!')
        return false
      }
      currentFile.value = file
      return false // 阻止自动上传
    }

    const beforeVideoUpload = (file) => {
      const isVideo = ['video/mp4', 'video/webm'].includes(file.type)
      const isLt50M = file.size / 1024 / 1024 < 50

      if (!isVideo) {
        ElMessage.error('只能上传 mp4/webm 格式的视频!')
        return false
      }
      if (!isLt50M) {
        ElMessage.error('视频大小不能超过 50MB!')
        return false
      }
      currentVideo.value = file
      return false // 阻止自动上传
    }

    const handleFileChange = (file) => {
      if (file && file.raw) {
        currentFile.value = file.raw
        form.value.image = file.raw
      }
    }

    const handleVideoChange = (file) => {
      if (file && file.raw) {
        currentVideo.value = file.raw
        form.value.video = file.raw
      }
    }

    const handleUploadSuccess = (response) => {
      if (response && response.filename) {
        form.value.image = response.filename
        ElMessage.success('图片上传成功')
        return true
      } else {
        ElMessage.error('上传响应格式错误')
        return false
      }
    }

    const handleVideoUploadSuccess = (response) => {
      if (response && response.filename) {
        form.value.video = response.filename
        ElMessage.success('视频上传成功')
        return true
      } else {
        ElMessage.error('上传响应格式错误')
        return false
      }
    }

    const handleUploadError = (error) => {
      console.error('上传失败:', error)
      ElMessage.error(error.message || '上传失败')
      return false
    }

    // 搜索
    const handleSearch = () => {
      fetchTutorials()
    }

    // 分页大小改变
    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchTutorials()
    }

    // 页码改变
    const handleCurrentChange = (val) => {
      currentPage.value = val
      fetchTutorials()
    }

    onMounted(() => {
      fetchTutorials()
    })

    return {
      tutorials,
      loading,
      dialogVisible,
      dialogType,
      submitting,
      form,
      formRef,
      rules,
      fileList,
      videoFileList,
      uploadHeaders,
      searchQuery,
      showAddDialog,
      handleEdit,
      handleDelete,
      handleSubmit,
      handleUploadSuccess,
      handleVideoUploadSuccess,
      handleUploadError,
      beforeUpload,
      beforeVideoUpload,
      handleFileChange,
      handleVideoChange,
      handleSearch,
      currentPage,
      pageSize,
      total,
      handleSizeChange,
      handleCurrentChange
    }
  }
}
</script>

<style scoped>
.tutorial-manage {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-bar {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.search-input {
  width: 300px;
}

.preview-image {
  width: 80px;
  height: 80px;
  border-radius: 4px;
  object-fit: cover;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.el-upload__tip {
  color: #909399;
  font-size: 12px;
  margin-top: 7px;
}
</style>